import React from "react";
import { observer } from "mobx-react-lite";
import { Tooltip } from "@heroui/react";
import { contractorsStore } from "@/stores/ContractorsStore";
import Sparkline from "./Sparkline";
import { NoDataPlaceholder } from "@/features/general";
import { MetricsSkeleton } from "./MetricSkeleton";

function formatPeriodLabel(month: Date): string {
  const now = new Date();
  const isCurrentMonth =
    month.getFullYear() === now.getFullYear() &&
    month.getMonth() === now.getMonth();
  if (isCurrentMonth) {
    const day = now.getDate();
    const monthShort = month.toLocaleString("ru-RU", { month: "long" });
    return `с 1 по ${day} ${monthShort} ${now.getFullYear()}`;
  }
  return month
    .toLocaleString("ru-RU", { month: "long", year: "numeric" })
    .replace(/\s*г\.?\s*$/i, "");
}

function formatPrevPeriodLabel(month: Date): string {
  const now = new Date();
  const isCurrentMonth =
    month.getFullYear() === now.getFullYear() &&
    month.getMonth() === now.getMonth();
  const prev = new Date(month.getFullYear(), month.getMonth() - 1, 1);
  if (isCurrentMonth) {
    const day = now.getDate();
    const prevMonthShort = prev.toLocaleString("ru-RU", { month: "long" });
    return `с 1 по ${day} ${prevMonthShort} ${prev.getFullYear()}`;
  }
  return prev
    .toLocaleString("ru-RU", { month: "long", year: "numeric" })
    .replace(/\s*г\.?\s*$/i, "");
}

function formatCompact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} млн ₽`;
  if (value >= 1_000) return `${Math.round(value / 1_000)} тыс ₽`;
  return `${value} ₽`;
}

function formatChange(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "—";
  const sign = value >= 0 ? "+" : "−";
  return `${sign}${Math.abs(Math.round(value))}%`;
}

interface MetricDef {
  label: string;
  value: string;
  change: number | null;
  sparkData: number[];
  color: string;
  description: string;
}

const MetricsPanel: React.FC = observer(() => {
  const { summary, isSummaryLoading, activeMonth } = contractorsStore;

  if (isSummaryLoading || !summary) {
    return <MetricsSkeleton />;
  }

  const charts = {
    totalBookings: summary.charts?.totalBookings ?? [],
    appBookings: summary.charts?.appBookings ?? [],
    totalRevenue: summary.charts?.totalRevenue ?? [],
    totalCommission: summary.charts?.totalCommission ?? [],
  };

  const metrics: MetricDef[] = [
    {
      label: "Бронирования",
      value: summary.totalBookings.toLocaleString("ru-RU"),
      change: summary.totalBookingsChange ?? null,
      sparkData: charts.totalBookings,
      color: "#6366f1",
      description: "Общее количество бронирований через платформу по всем СТО",
    },
    {
      label: "Через приложение",
      value: summary.appBookings.toLocaleString("ru-RU"),
      change: summary.appBookingsChange ?? null,
      sparkData: charts.appBookings,
      color: "#06b6d4",
      description: "Бронирования, оформленные через мобильное приложение",
    },
    {
      label: "Доход",
      value: formatCompact(summary.totalRevenue),
      change: summary.totalRevenueChange ?? null,
      sparkData: charts.totalRevenue,
      color: "#22c55e",
      description: "Суммарный оборот всех СТО по платежам клиентов",
    },
    {
      label: "Комиссия",
      value: formatCompact(summary.totalCommission),
      change: summary.totalCommissionChange ?? null,
      sparkData: charts.totalCommission,
      color: "#f59e0b",
      description: "Доход платформы: комиссия с бронирований всех СТО",
    },
  ];

  return (
    <div className="sc-metrics" data-testid="contractors-metrics">
      {metrics.map((m) => (
        <div key={m.label} className="sc-metric-card">
          <div className="sc-metric-card__info">
            <span className="sc-metric-card__label">{m.label}</span>
            <Tooltip delay={300}>
              <Tooltip.Trigger>
                <span className="sc-metric-card__value sc-metric-card__value--tip">
                  {m.value}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content className="sc-metric-tooltip">
                <p className="sc-metric-tooltip__desc">{m.description}</p>
                <p className="sc-metric-tooltip__period">
                  Период: {formatPeriodLabel(activeMonth)}
                </p>
              </Tooltip.Content>
            </Tooltip>
            <Tooltip delay={300}>
              <Tooltip.Trigger>
                <span
                  className={`sc-metric-card__change sc-metric-card__change--tip ${
                    m.change === null
                      ? ""
                      : m.change >= 0
                        ? "sc-metric-card__change--positive"
                        : "sc-metric-card__change--negative"
                  }`}
                >
                  {formatChange(m.change)}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content className="sc-metric-tooltip">
                <p className="sc-metric-tooltip__desc">
                  Изменение относительно аналогичного периода прошлого месяца
                </p>
                <p className="sc-metric-tooltip__period">
                  Сравнение: {formatPrevPeriodLabel(activeMonth)}
                </p>
              </Tooltip.Content>
            </Tooltip>
          </div>
          {m.sparkData.length > 0 ? (
            <Sparkline data={m.sparkData} color={m.color} />
          ) : (
            <NoDataPlaceholder />
          )}
        </div>
      ))}
    </div>
  );
});

MetricsPanel.displayName = "MetricsPanel";
export default MetricsPanel;
